import { injectable, inject } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type WindowService from '@/services/browser/windowService'
import type { TmBaseEvent, TmBaseEventConstructor } from '@/core/events/TmBaseEvent'
import type { PerformanceMarkService } from '@/services/performanceMarkService'

@injectable()
export class EventBusService {
  private readonly target: EventTarget

  private listeners: Map<symbol, { eventId: string; listener: EventListener }> = new Map()

  constructor(
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
    @inject(SERVICE_TYPES.PerformanceMarkService) protected readonly performanceMarkService: PerformanceMarkService,
  ) {
    this.target = this.windowService.self()
  }

  public emit<T extends TmBaseEvent>(event: T) {
    this.target.dispatchEvent(event)
    this.performanceMarkService.mark(`EventBus emit: ${event.id}`)
  }

  public subscribe<T extends TmBaseEvent>(event: TmBaseEventConstructor<T>, listener: (event: T) => void) {
    const subscriptionId = Symbol(event.id)

    const wrappedListener = (e: Event) => listener(e as T)
    this.target.addEventListener(event.id, wrappedListener)
    this.listeners.set(subscriptionId, {
      eventId: event.id,
      listener: wrappedListener,
    })

    return subscriptionId
  }

  public unsubscribe(subscriptionId: symbol) {
    const event = this.listeners.get(subscriptionId)
    if (event) {
      this.target.removeEventListener(event.eventId, event.listener)
      this.listeners.delete(subscriptionId)
    }
  }

  public cleanup() {
    this.listeners.forEach((data, id) => {
      this.target.removeEventListener(data.eventId, data.listener)
    })
    this.listeners.clear()
  }
}
